import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import { formatMoney } from '../lib/format';

interface ManagedBooking {
  pnr: string;
  status: string;
  totalAmount: number;
  currency: string;
  nrs?: { pnr: string };
}

export function ManagePage() {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const checkin = mode === 'checkin';
  const refund = mode === 'refund';
  const change = mode === 'change';
  const [pnr, setPnr] = useState(params.get('pnr') ?? '');
  const [lastName, setLastName] = useState(params.get('lastName') ?? '');
  const [flightNumber, setFlightNumber] = useState(params.get('flight') ?? '');
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState<{ pnr?: string; lastName?: string; flight?: string }>({});
  const [loading, setLoading] = useState(false);

  async function lookup(reference = pnr, name = lastName) {
    setError('');
    setLoading(true);
    try {
      const qs = new URLSearchParams({ pnr: reference, lastName: name });
      const data = await apiFetch<{ booking: ManagedBooking }>(`/api/v1/bookings/manage?${qs}`);
      setBooking(data.booking);
    } catch (err) {
      setBooking(null);
      setError(err instanceof Error ? err.message : t('common.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  const searchKey = params.toString();
  useEffect(() => {
    const current = new URLSearchParams(searchKey);
    const reference = current.get('pnr') ?? '';
    const name = current.get('lastName') ?? '';
    if (reference && name) void lookup(reference, name);
  }, [searchKey]);

  function onCheckinSubmit(e: FormEvent) {
    e.preventDefault();
    const next: typeof fieldError = {};
    if (pnr.trim().length < 5) next.pnr = t('common.requiredField');
    if (lastName.trim().length < 2) next.lastName = t('common.requiredField');
    if (!/^[A-Za-z0-9]{2,8}$/.test(flightNumber.trim())) next.flight = t('common.requiredField');
    setFieldError(next);
    if (Object.keys(next).length) return;
    void lookup();
  }

  if (checkin) {
    return (
      <main className="sa-page checkin-page">
        <article className="checkin-card">
          <div className="checkin-card__panel">
            <h1 className="checkin-card__title">{t('common.checkinLead')}</h1>
            <form className="checkin-card__form" onSubmit={onCheckinSubmit}>
              <CheckinField
                id="checkin-ref"
                label={t('common.bookingOrTicket')}
                value={pnr}
                error={fieldError.pnr}
                dir="ltr"
                autoComplete="off"
                onChange={(value) => {
                  setPnr(value.toUpperCase());
                  setFieldError((current) => ({ ...current, pnr: undefined }));
                }}
              />
              <CheckinField
                id="checkin-name"
                label={t('common.tripsLastName')}
                value={lastName}
                error={fieldError.lastName}
                dir="ltr"
                autoComplete="family-name"
                onChange={(value) => {
                  setLastName(value);
                  setFieldError((current) => ({ ...current, lastName: undefined }));
                }}
              />
              <CheckinField
                id="checkin-flight"
                label={t('common.checkinFlightNumber')}
                value={flightNumber}
                error={fieldError.flight}
                dir="ltr"
                inputMode="text"
                autoComplete="off"
                onChange={(value) => {
                  setFlightNumber(value.toUpperCase());
                  setFieldError((current) => ({ ...current, flight: undefined }));
                }}
              />
              <button className="checkin-card__submit" type="submit" disabled={loading}>
                {loading ? t('common.loading') : t('common.findFlight')}
              </button>
            </form>

            {error ? <div className="sa-alert sa-alert--error checkin-card__alert">{error}</div> : null}

            {booking ? (
              <div className="checkin-card__result">
                <span className="sa-badge">
                  {t(`common.bookingStatus.${booking.status}`, { defaultValue: booking.status })}
                </span>
                <p className="checkin-card__pnr" dir="ltr">
                  {booking.nrs?.pnr ?? booking.pnr}
                </p>
                {booking.nrs ? (
                  <p className="sa-text-muted">
                    {t('common.airlineReference')} · {t('common.pnr')}: <span dir="ltr">{booking.pnr}</span>
                  </p>
                ) : null}
                <p className="sa-text-muted">
                  {t('common.total')}: {formatMoney(booking.totalAmount, booking.currency, isFa)}
                </p>
              </div>
            ) : null}

            <footer className="checkin-card__contact">
              <a href="tel:+982191000000">
                <PhoneIcon />
                <span dir="ltr">{t('common.checkinPhone')}</span>
              </a>
              <a href="mailto:checkin@soroushair.com">
                <MailIcon />
                <span dir="ltr">{t('common.checkinEmail')}</span>
              </a>
            </footer>
          </div>
          <div className="checkin-card__art" aria-hidden="true">
            <CheckinArt />
          </div>
        </article>
      </main>
    );
  }

  return (
    <main className="sa-page">
      <div className="sa-container" style={{ maxWidth: '40rem' }}>
        <div className="sa-page__header">
          <p className="home-section__eyebrow">
            {checkin || refund || change ? t('nav.groups.book') : t('common.retrieveBooking')}
          </p>
          <h1 className="sa-headline">
            {checkin
              ? t('nav.items.checkin')
              : refund
                ? t('nav.items.refund')
                : change
                  ? t('nav.items.change')
                  : t('common.manageBooking')}
          </h1>
          {checkin ? <p className="sa-text-muted">{t('common.servicesCheckinNote')}</p> : null}
          {refund ? <p className="sa-text-muted">{t('nav.leads.refund')}</p> : null}
          {change ? <p className="sa-text-muted">{t('nav.leads.change')}</p> : null}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void lookup();
          }}
          className="sa-grid sa-grid--2"
        >
          <Input
            label={t('common.pnr')}
            value={pnr}
            onChange={(e) => setPnr(e.target.value.toUpperCase())}
            maxLength={6}
            required
          />
          <Input
            label={checkin ? t('common.tripsLastName') : t('common.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          {checkin ? (
            <Input
              label={t('common.checkinFlightNumber')}
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
              required
            />
          ) : null}
          <div>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading') : t('common.continue')}
            </Button>
          </div>
        </form>

        {error ? <div className="sa-alert sa-alert--error">{error}</div> : null}

        {booking ? (
          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--sa-color-border)' }}>
            <span className="sa-badge">
              {t(`common.bookingStatus.${booking.status}`, { defaultValue: booking.status })}
            </span>
            <p
              dir="ltr"
              style={{ marginTop: '1rem', fontSize: '1.75rem', fontWeight: 700, color: 'var(--sa-gold-deep)' }}
            >
              {booking.nrs?.pnr ?? booking.pnr}
            </p>
            {booking.nrs ? (
              <p className="sa-text-muted">
                {t('common.airlineReference')} · {t('common.pnr')}: <span dir="ltr">{booking.pnr}</span>
              </p>
            ) : null}
            <p className="sa-text-muted">
              {t('common.total')}: {formatMoney(booking.totalAmount, booking.currency, isFa)}
            </p>
          </div>
        ) : null}
      </div>
    </main>
  );
}

function CheckinField({
  id,
  label,
  value,
  error,
  onChange,
  dir,
  autoComplete,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  dir?: 'ltr' | 'rtl';
  autoComplete?: string;
  inputMode?: 'text' | 'numeric';
}) {
  return (
    <div className={`checkin-field${error ? ' is-invalid' : ''}`}>
      <input
        id={id}
        value={value}
        placeholder=" "
        dir={dir}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <label htmlFor={id}>
        {label}
        <span className="checkin-field__req" aria-hidden="true">
          *
        </span>
      </label>
      {error ? (
        <p id={`${id}-error`} className="checkin-field__error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M7.2 3.6h2.1c.4 0 .8.3.9.7l.8 2.5a1 1 0 0 1-.3 1L9.2 9.2a12.4 12.4 0 0 0 5.6 5.6l1.4-1.5a1 1 0 0 1 1-.3l2.5.8c.4.1.7.5.7.9v2.1c0 .6-.4 1-1 1C11.8 17.8 6.2 12.2 6.2 4.6c0-.6.4-1 1-1z"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.2 6.2h15.6c.7 0 1.2.5 1.2 1.2v9.2c0 .7-.5 1.2-1.2 1.2H4.2c-.7 0-1.2-.5-1.2-1.2V7.4c0-.7.5-1.2 1.2-1.2zm7.8 6.3 7.1-4.5H4.9l7.1 4.5zm0 1.5L4.5 9.3v7.3h15V9.3l-7.5 4.7z"
      />
    </svg>
  );
}

function CheckinArt() {
  return (
    <svg viewBox="0 0 673 587" preserveAspectRatio="xMinYMid slice" xmlns="http://www.w3.org/2000/svg">
      <rect width="673" height="587" fill="#fbf8f2" />
      <circle cx="-30" cy="294" r="560" fill="#f3e7cc" />
      <circle cx="-30" cy="294" r="448" fill="#fbf8f2" />
      <circle cx="-30" cy="294" r="378" fill="#e6d2a6" />
      <circle cx="-30" cy="294" r="286" fill="#fbf8f2" />
      <circle cx="-30" cy="294" r="228" fill="#d4b56e" />
      <circle cx="-30" cy="294" r="156" fill="#fbf8f2" />
      <circle cx="-30" cy="294" r="104" fill="#b8923f" />
      <circle cx="-30" cy="294" r="46" fill="#fbf8f2" />
    </svg>
  );
}
